import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import { useGetCatalogBrowseQuery } from '../../store/api/stocktakeApi';
import { useGetPickingCatalogBrowseQuery } from '../../store/api/pickingApi';
import { useGetProductsCatalogBrowseQuery } from '../../store/api/productsApi';
import { terminalColors } from '../../theme/terminalStyles';

const pickerPaperSx = {
  width: { xs: 'calc(100% - 16px)', sm: 600 },
  maxWidth: { xs: 'calc(100% - 16px)', sm: 600 },
  height: { xs: 'calc(100% - 16px)', sm: 'min(800px, calc(100% - 64px))' },
  maxHeight: { xs: 'calc(100% - 16px)', sm: 'min(800px, calc(100% - 64px))' },
  m: { xs: 1, sm: 4 },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  bgcolor: terminalColors.gradientTop,
  backgroundImage: 'none',
  borderRadius: 2,
};

const ProductPickerDialog = ({
  open,
  stocktakeId,
  spickingId,
  standalone = false,
  title,
  initialSearch = '',
  onClose,
  onSelectProduct,
}) => {
  const [search, setSearch] = useState('');
  const [parentId, setParentId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const isPicking = Boolean(spickingId) && !standalone;
  const isStocktake = Boolean(stocktakeId) && !standalone;
  const isStandalone = Boolean(standalone) || (!isPicking && !isStocktake);
  const documentKey = isStandalone
    ? 'standalone'
    : isPicking
      ? `p-${spickingId}`
      : `s-${stocktakeId}`;

  useEffect(() => {
    setSearch('');
    setParentId(null);
    setFolderPath([]);
  }, [documentKey]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    if (!initialSearch) return;
    setSearch(initialSearch);
    setParentId(null);
    setFolderPath([]);
  }, [open, initialSearch]);

  const trimmedSearch = search.trim();
  const stocktakeQuery = useGetCatalogBrowseQuery(
    {
      stocktakeId,
      parentId: trimmedSearch ? undefined : parentId,
      search: trimmedSearch || undefined,
    },
    { skip: !open || !stocktakeId || isPicking || isStandalone }
  );
  const pickingQuery = useGetPickingCatalogBrowseQuery(
    {
      spickingId,
      parentId: trimmedSearch ? undefined : parentId,
      search: trimmedSearch || undefined,
    },
    { skip: !open || !spickingId || isStandalone }
  );
  const productsQuery = useGetProductsCatalogBrowseQuery(
    {
      parentId: trimmedSearch ? undefined : parentId,
      search: trimmedSearch || undefined,
    },
    { skip: !open || !isStandalone }
  );

  const data = isStandalone
    ? productsQuery.data
    : isPicking
      ? pickingQuery.data
      : stocktakeQuery.data;
  const isFetching = isStandalone
    ? productsQuery.isFetching
    : isPicking
      ? pickingQuery.isFetching
      : stocktakeQuery.isFetching;

  const categories = data?.categories || [];
  const products = data?.products || [];

  const enterCategory = (cat) => {
    setFolderPath((prev) => [...prev, { id: cat.id, name: cat.name }]);
    setParentId(cat.id);
    setSearch('');
  };

  const goToPathIndex = (index) => {
    if (index < 0) {
      setFolderPath([]);
      setParentId(null);
    } else {
      const next = folderPath.slice(0, index + 1);
      setFolderPath(next);
      setParentId(next[next.length - 1].id);
    }
    setSearch('');
  };

  const goBack = () => {
    goToPathIndex(folderPath.length - 2);
  };

  const crumbSx = {
    border: 0,
    background: 'none',
    p: 0,
    font: 'inherit',
    cursor: 'pointer',
    textAlign: 'left',
  };

  const dialogTitle = title || (isStandalone ? 'Вибрати товар' : 'Додати товар');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: pickerPaperSx }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, flexShrink: 0 }}>
        {parentId != null && !trimmedSearch ? (
          <IconButton aria-label="назад" onClick={goBack} edge="start">
            <ArrowBackIcon />
          </IconButton>
        ) : null}
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          {dialogTitle}
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Пошук"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1, flexShrink: 0 }}
        />
        <Breadcrumbs
          separator="›"
          maxItems={4}
          itemsBeforeCollapse={1}
          itemsAfterCollapse={2}
          sx={{
            mb: 1,
            flexShrink: 0,
            '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' },
          }}
        >
          <Link
            component="button"
            type="button"
            underline={folderPath.length ? 'hover' : 'none'}
            color={folderPath.length ? 'inherit' : 'text.primary'}
            onClick={() => goToPathIndex(-1)}
            sx={{ ...crumbSx, fontWeight: folderPath.length ? 400 : 600 }}
          >
            Каталог
          </Link>
          {folderPath.map((crumb, idx) => {
            const isLast = idx === folderPath.length - 1;
            if (isLast) {
              return (
                <Typography key={crumb.id} color="text.primary" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {crumb.name}
                </Typography>
              );
            }
            return (
              <Link
                key={crumb.id}
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                onClick={() => goToPathIndex(idx)}
                sx={{ ...crumbSx, wordBreak: 'break-word' }}
              >
                {crumb.name}
              </Link>
            );
          })}
        </Breadcrumbs>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            position: 'relative',
          }}
        >
          {isFetching ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100%',
                py: 3,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense disablePadding>
              {!trimmedSearch
                ? categories.map((cat) => (
                    <ListItemButton key={`cat-${cat.id}`} onClick={() => enterCategory(cat)}>
                      <FolderOutlinedIcon sx={{ mr: 1.5, color: terminalColors.accentOrangeHover }} />
                      <ListItemText primary={cat.name} />
                    </ListItemButton>
                  ))
                : null}
              {products.map((p) => {
                const inDoc = isStandalone ? false : isPicking ? p.inPicking : p.inStocktake;
                const qty = isPicking ? p.quantity : p.quantityActual;
                return (
                  <ListItemButton
                    key={`prod-${p.id}`}
                    onClick={() => onSelectProduct(p)}
                    sx={inDoc ? { bgcolor: 'rgba(46, 125, 50, 0.08)' } : undefined}
                  >
                    <Box
                      sx={{
                        width: 24,
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Inventory2OutlinedIcon sx={{ fontSize: 12, color: '#5B8DEF' }} />
                    </Box>
                    <ListItemText
                      primary={`${p.name || ''} ${p.modName || ''}`.trim()}
                      secondary={
                        <>
                          {p.article ? `Арт: ${p.article}` : null}
                          {String(p.barcode || '').trim()
                            ? `${p.article ? ' · ' : ''}ШК: ${String(p.barcode).trim()}`
                            : null}
                          {inDoc ? (isPicking ? ` · Зібрано: ${qty}` : ` · Факт: ${qty}`) : null}
                        </>
                      }
                    />
                  </ListItemButton>
                );
              })}
              {!categories.length && !products.length ? (
                <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                  Нічого не знайдено
                </Typography>
              ) : null}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, flexShrink: 0 }}>
        <TerminalOrangeButton onClick={onClose}>Закрити</TerminalOrangeButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProductPickerDialog;
